// Filter-Zustand ↔ geteilte Bausteine — die Übersetzung dazwischen, an EINER Stelle.
//
// `Filterleiste.tsx` und `SeitenblaetterFuss.tsx` haben die FLÄCHE vereinheitlicht (Raster,
// Polster, Beschriftung, Verdrahtung, Sperrbedingungen). Der Kleber daneben blieb kopiert:
// dieselben Optionen-Paare, dieselbe Hin- und Rückrechnung zwischen Filterwert und
// `<option>`-Wert, derselbe Seitenwechsel-Rückruf. Hier steht er einmal.
//
// ⚠️ **Warum diese Datei überhaupt existiert und die Sachen nicht in den beiden Bausteinen stehen:**
// `eslint-plugin-react-refresh` verbietet einer Datei, die eine Komponente exportiert, zusätzliche
// Wert-Exporte — sonst verliert Fast Refresh beim Speichern den Zustand. Typen sind davon
// ausgenommen, Funktionen nicht. `FilterAuswahl` und `GebundeneAuswahl` stehen deshalb hier neben
// den Funktionen, die sie erzeugen, statt bei der Komponente, die sie rendert.
//
// ⚠️ Aufgenommen ist NUR, was gemessen mehrfach buchstabengleich dastand:
//
// | Kleber        | Audio | Video | MindBooks | Inhalte    | Benutzer   |
// |---------------|-------|-------|-----------|------------|------------|
// | Status        | ✓     | ✓     | ✓         | ✓          | ABWEICHEND |
// | Zugriff       | ✓     | ✓     | ✓         | —          | —          |
// | Produkttyp    | ✓     | ✓     | —         | —          | —          |
// | Seitenwechsel | ✓     | ✓     | ✓         | ✓          | ✓          |
//
// - **Status** ist bei Audio/Video/MindBooks/Inhalten viermal dasselbe. Bis 2026-08-13 stand
//   die Inhalte-Seite hier auf ABWEICHEND, und der Grund dafür war ein echter: die drei
//   Medien-Seiten hatten je ein eigenes Status-Enum mit nur drei Stufen, `ContentStatus` kennt
//   vier — `statusAuswahl` hätte auf der Inhalte-Seite `REJECTED` still verschluckt. Die drei
//   Enums sind aufgelöst (alle vier Seiten führen jetzt `ContentStatus`), also fällt der Grund
//   weg und die vierte Handkopie mit ihm. Dass ein abgelehnter Inhalt AUFFINDBAR sein muss,
//   gilt für Audio/Video/MindBooks genauso: die Listen-Route dieser drei filtert über dieselbe
//   Backend-Funktion `normalizeStatus`, die `REJECTED` annimmt.
// - **Die Benutzer-Seite bleibt draußen**: ihr „Status" ist `'active' | 'inactive'` — ein anderer
//   Begriff mit demselben Namen. Zusammengelegt hieße das, Konto-Zustand und
//   Veröffentlichungs-Stufe für dasselbe zu halten.
// - **Produkttyp** unterscheidet sich zwischen Audio und Video nur in der LISTE
//   (`audioProductTypes` / `videoProductTypes`); Beschriftung, Text-Quelle und Rückrechnung waren
//   identisch. Die Liste ist deshalb ein Parameter.
//
// Seit 2026-08-13 kommen Alben, Interpreten und Playlists als Verbraucher dazu. Sie holen ihren
// Status NICHT aus `statusAuswahl`, sondern aus `kuratierStatusAuswahl` weiter unten — ihre drei
// Prisma-Enums kennen nur `DRAFT` und `PUBLISHED`. Die Begründung steht dort; sie ist dieselbe,
// aus der die Benutzer-Seite in der Tabelle oben auf ABWEICHEND steht.
import type React from 'react';
import { ContentStatus, STATUS_ANZEIGE } from '../../types';
import { ProductType, ProductTypeLabels } from '../../types/productType';

/**
 * Eine Auswahlliste, deren Wert-Typ aus ihren eigenen Optionen folgt.
 *
 * ⚠️ Der Punkt dieses Typ-Parameters: der Rückruf bekam früher `(wert: string)`, und jeder
 * Verbraucher schrieb deshalb `as ContentStatus | undefined` daneben. Eine solche Zusicherung wird
 * NICHT geprüft — driftet die Optionsliste, behauptet sie weiter einen Typ, den der Wert nicht
 * mehr hat, und der Compiler schweigt. Hier bindet `W` die Optionen und den Rückruf aneinander:
 * eine Option, die nicht in den Filter passt, ist ab jetzt ein Übersetzungsfehler.
 */
export interface FilterAuswahl<W extends string> {
  /** Sichtbare Beschriftung über der Liste, z. B. „Status". */
  beschriftung: string;
  /** Aktueller Wert; `undefined` heißt „Alle". */
  wert: W | undefined;
  /** Bekommt einen Wert AUS `optionen` oder `undefined` für „Alle" — nie etwas anderes. */
  onChange: (wert: W | undefined) => void;
  optionen: ReadonlyArray<{ wert: W; text: string }>;
}

/**
 * Eine Auswahl, deren Wert-Typ bereits gebunden ist — die Form, in der die Leiste sie rendert.
 *
 * Warum es diese zweite Form überhaupt gibt: `auswahlen` ist eine gemischte Liste (Status ist ein
 * `ContentStatus`, Zugriff ein `'free' | 'premium'`, Rolle ein `UserRole`). TypeScript kennt keine
 * existenziellen Typen, mit denen ein Array „je Element ein eigenes W" ausdrücken könnte. Der
 * übliche Ersatz dafür ist genau das hier: eine generische Funktion (`filterAuswahl`) bindet das
 * `W` an der Aufrufstelle und gibt eine Form ohne Typ-Parameter zurück.
 *
 * ⚠️ Rest-Lücke, bewusst offen gelassen: dieser Typ ist strukturell. Ein handgeschriebenes Objekt
 * mit `onChange: (wert: string) => void` würde weiterhin angenommen; erzwungen ist der Weg über
 * `filterAuswahl` nicht. Ein Marken-Symbol (`unique symbol`) würde das schließen, machte den Typ
 * aber undurchsichtig — für fünf Aufrufstellen, die alle über `filterAuswahl` laufen und deren
 * Gebrauch pro Seite getestet ist, kein guter Tausch.
 */
export interface GebundeneAuswahl {
  beschriftung: string;
  /** Leere Zeichenkette = „Alle" — das ist der rohe Wert des `<option>`, nicht der Filterwert. */
  wert: string;
  onChange: (roherWert: string) => void;
  optionen: ReadonlyArray<{ wert: string; text: string }>;
}

/**
 * Bindet eine Auswahl an ihren Wert-Typ. Ersetzt die Zusicherungen an den Aufrufstellen.
 *
 * ⚠️ Der gemeldete Wert wird in `optionen` NACHGESCHLAGEN, statt mit `as W` behauptet zu werden.
 * Das ist der eigentliche Gewinn: der Wert, den der Verbraucher bekommt, stammt beweisbar aus
 * seiner eigenen Optionsliste. Steht im `<select>` ein Wert, den es dort nicht (mehr) gibt, meldet
 * die Leiste `undefined` (= „Alle") — kein erfundener Filterwert, der stumm ans Backend ginge.
 */
export const filterAuswahl = <W extends string>(auswahl: FilterAuswahl<W>): GebundeneAuswahl => ({
  beschriftung: auswahl.beschriftung,
  wert: auswahl.wert ?? '',
  optionen: auswahl.optionen,
  onChange: (roherWert) => {
    if (roherWert === '') {
      auswahl.onChange(undefined);
      return;
    }
    auswahl.onChange(auswahl.optionen.find((option) => option.wert === roherWert)?.wert);
  },
});

/**
 * Anzeige-Rang je Veröffentlichungs-Stufe im Filter: veröffentlicht zuerst, die Ausnahmefälle
 * hinten. Genau diese Reihenfolge stand vorher in allen Handkopien.
 *
 * ⚠️ Warum ein Rang-Verzeichnis und keine geschriebene Liste: `satisfies Record<ContentStatus, …>`
 * erzwingt, dass JEDE Stufe des Enums hier einen Rang hat. Eine fünfte Stufe im Backend-Vertrag
 * macht diese Zeile rot, statt still aus dem Filter zu fallen — das ist der Fehler, der diese
 * ganze Runde ausgelöst hat, nur eine Ebene höher. Eine handgeschriebene Liste (auch mit
 * `as const`) könnte das nicht: sie wäre bei vier von fünf Stufen weiterhin gültig.
 */
const STATUS_RANG = {
  [ContentStatus.PUBLISHED]: 0,
  [ContentStatus.DRAFT]: 1,
  [ContentStatus.ARCHIVED]: 2,
  [ContentStatus.REJECTED]: 3,
} satisfies Record<ContentStatus, number>;

// `Object.values` liefert beweisbar alle Stufen (Typ `ContentStatus[]`, keine Zusicherung nötig);
// der Rang bringt sie in die Anzeige-Reihenfolge statt in die Deklarations-Reihenfolge.
const STATUS_REIHENFOLGE: ContentStatus[] = Object.values(ContentStatus).sort(
  (a, b) => STATUS_RANG[a] - STATUS_RANG[b],
);

/**
 * Die Veröffentlichungs-Stufen als Auswahlliste — für alle vier Inhalts-Seiten dieselbe.
 *
 * ⚠️ Hier stand ein Typ-Parameter `W` und ein Argument `stufen`, weil jede Seite ihr eigenes
 * Status-Enum mitbrachte. Genau das war der Fehler: es gibt nur EIN Status-Enum für Inhalte
 * (`ContentStatus`, gespiegelt aus Prisma). Ohne das Argument kann keine Seite mehr eine
 * unvollständige Fassung hereinreichen.
 *
 * ⚠️ **Der Filter bietet `REJECTED` an, die FORMULARE nicht.** Das ist kein Widerspruch: Finden
 * ist nicht Setzen. Abgelehnt wird über `POST /api/content/:id/reject`, und diese Route ist im
 * Backend auf ADMIN/MODERATOR gesperrt — den Status setzt also eine Prüfung, nicht der Autor.
 * Ein abgelehnter Inhalt muss trotzdem auffindbar bleiben, sonst ist er nur noch über die
 * Volltextsuche erreichbar. Die Listen-Routen aller vier Inhalts-Arten nehmen den Wert an (sie
 * laufen durch dieselbe Backend-Funktion `normalizeStatus`).
 *
 * ⚠️ Die Texte kommen aus `STATUS_ANZEIGE`, nicht aus handgeschriebenen Zeichenketten. Das ist
 * dieselbe Quelle, aus der die Status-Abzeichen der Tabelle lesen. Vorher standen die Wörter
 * „Veröffentlicht/Entwurf/Archiviert" im Filter noch einmal getrennt daneben — und genau so waren
 * Filter und Abzeichen im CMS schon einmal auseinandergelaufen.
 */
export const statusAuswahl = (
  wert: ContentStatus | undefined,
  onChange: (wert: ContentStatus | undefined) => void,
): GebundeneAuswahl =>
  filterAuswahl<ContentStatus>({
    beschriftung: 'Status',
    wert,
    onChange,
    optionen: STATUS_REIHENFOLGE.map((stufe) => ({
      wert: stufe,
      text: STATUS_ANZEIGE[stufe]?.text ?? stufe,
    })),
  });

/**
 * Die Stufen der KURATIERTEN Entitäten (Alben, Interpreten, Playlists) — zwei, nicht vier.
 *
 * ⚠️ Warum das nicht `statusAuswahl` von oben sein darf, obwohl beide „Status" heißen und beide
 * ihre Texte aus `STATUS_ANZEIGE` ziehen: `ContentStatus` hat vier Stufen, diese drei Entitäten
 * haben zwei. Am Schema nachgemessen, nicht aus den CMS-Typen geschlossen — `schema.prisma` führt
 * `enum AlbumStatus`, `enum ArtistStatus` und `enum PlaylistStatus` je mit genau `DRAFT` und
 * `PUBLISHED`. „Archiviert" und „Abgelehnt" wären also Filterwerte, die kein Datensatz je tragen
 * kann: die Liste liefe leer, und die Nutzerin läse das als „es gibt keine archivierten Alben"
 * statt als „diese Stufe gibt es hier nicht".
 *
 * Das ist derselbe Grund, aus dem die Benutzer-Verwaltung mit ihrem `'active' | 'inactive'`
 * draußen bleibt (siehe Tabelle im Kopf): gleicher Name, anderer Begriff.
 *
 * ⚠️ Die Stufenliste ist die QUELLE des Typs, nicht seine Kopie — `KuratierStatus` wird aus
 * `KURATIER_STUFEN` abgeleitet. Eine Stufe, die hier fehlt, kann deshalb nicht als Filterwert
 * existieren; eine zusätzliche steht automatisch im Auswahlfeld.
 *
 * ⚠️ Und die Gegenrichtung — driftet der Vertrag, wird es rot statt still: die drei Seiten führen
 * ihren Filterwert in ihrem EIGENEN Typ (`AlbumStatus`, `ArtistStatus`, `PlaylistStatus`). Bekäme
 * eine davon eine dritte Stufe, wäre sie nicht mehr auf `KuratierStatus` zuweisbar und der Aufruf
 * hier schlüge im Typecheck fehl. Eine Verengung ginge durch, eine Erweiterung nicht — und nur die
 * Erweiterung ist der Fall, der eine Stufe stillschweigend aus dem Filter fallen ließe.
 */
const KURATIER_STUFEN = ['PUBLISHED', 'DRAFT'] as const;

type KuratierStatus = (typeof KURATIER_STUFEN)[number];

export const kuratierStatusAuswahl = (
  wert: KuratierStatus | undefined,
  onChange: (wert: KuratierStatus | undefined) => void,
): GebundeneAuswahl =>
  filterAuswahl<KuratierStatus>({
    beschriftung: 'Status',
    wert,
    onChange,
    // Reihenfolge wie oben bei `statusAuswahl`: veröffentlicht zuerst, der Entwurf dahinter.
    // Texte aus `STATUS_ANZEIGE`, also aus derselben Quelle wie die Abzeichen der Tabelle daneben.
    optionen: KURATIER_STUFEN.map((stufe) => ({
      wert: stufe,
      text: STATUS_ANZEIGE[stufe]?.text ?? stufe,
    })),
  });

/**
 * Zugriffsstufe als Auswahlliste — der einzige Filter, dessen Wert kein String ist.
 *
 * ⚠️ Hier steckte der eigentliche Kopier-Schaden: `isPremium` ist ein `boolean | undefined`, die
 * Auswahlliste kennt aber nur Zeichenketten. Die Umrechnung in beide Richtungen stand dreimal
 * buchstabengleich in den Seiten — inklusive der Falle, dass `undefined` (kein Filter) und `false`
 * (ausdrücklich „Free") NICHT dasselbe sind. Wer das einmal zu `wert ? 'premium' : 'free'`
 * verkürzt, filtert bei „Alle" plötzlich auf „Free".
 */
export const zugriffAuswahl = (
  wert: boolean | undefined,
  onChange: (wert: boolean | undefined) => void,
): GebundeneAuswahl =>
  filterAuswahl({
    beschriftung: 'Zugriff',
    wert: wert === undefined ? undefined : wert ? 'premium' : 'free',
    onChange: (roherWert) => onChange(roherWert === undefined ? undefined : roherWert === 'premium'),
    // Beschriftungen bewusst englisch: so stehen sie auch in der Spalte „Zugriff" der Tabellen und
    // in `ROLLEN_ANZEIGE`. Zwei Namen für dieselbe Stufe wären schlimmer als ein unübersetzter.
    optionen: [
      { wert: 'free', text: 'Free' },
      { wert: 'premium', text: 'Premium' },
    ],
  });

/**
 * Produkttyp als Auswahlliste — die Liste kommt von der Seite, alles andere von hier.
 *
 * Der Filterwert ist im Backend-Vertrag ein loser `string` (`AudioFilters.category`), die Optionen
 * sind es nicht. `typen.find(...)` statt einer Zusicherung: ein Wert, der nicht in der übergebenen
 * Liste steht, ergibt „Alle" statt eines Produkttyps, den diese Seite gar nicht führt.
 */
export const produkttypAuswahl = (
  typen: readonly ProductType[],
  wert: string | undefined,
  onChange: (wert: ProductType | undefined) => void,
): GebundeneAuswahl =>
  filterAuswahl<ProductType>({
    beschriftung: 'Produkttyp',
    wert: typen.find((typ) => typ === wert),
    onChange,
    optionen: typen.map((typ) => ({ wert: typ, text: ProductTypeLabels[typ] })),
  });

/**
 * Der Rückruf für `onSeitenwechsel` — einmal statt fünfmal abgeschrieben.
 *
 * ⚠️ Das ist kein Sparen von Zeichen. Genau diese Zeile stand bei allen fünf Verbrauchern
 * identisch da, und die Inhalte-Seite hatte sie einmal FALSCH: sie hing an
 * `handleFilterChange('page', …)`, und das setzte im selben Objekt `page: 1` hinter den
 * übergebenen Wert — „Weiter" lud wieder Seite 1. Die Regel dahinter ist die, die hier
 * festgehalten wird: **die Seitenzahl ist kein Filter.** Ein Filterwechsel holt die Liste an den
 * Anfang zurück, ein Seitenwechsel darf das nicht.
 *
 * Der Typ-Parameter hält den Filter-Zustand der Seite zusammen: es wird nur `page` gesetzt, alles
 * andere bleibt unberührt.
 */
export const seitenwechsel =
  <F extends { page?: number }>(setFilters: React.Dispatch<React.SetStateAction<F>>) =>
  (seite: number) =>
    setFilters((vorher) => ({ ...vorher, page: seite }));
